// ABOUTME: The "Nalu v1" theme — colors, typography, and logo drawn from the
// ABOUTME: "Nalu and the Swinging Stone" brand kit; registered in themeRegistry.ts

import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/500.css';
import '@fontsource/newsreader/600.css';
import '@fontsource/newsreader/700.css';
import '@fontsource/nunito-sans/400.css';
import '@fontsource/nunito-sans/500.css';
import '@fontsource/nunito-sans/600.css';
import '@fontsource/nunito-sans/700.css';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      secondary: string;
    };
    behaviorHeart: string;
    oneOnOneAccent: string;
    destructiveAccent: string;
  }

  interface PaletteOptions {
    gradient?: {
      primary?: string;
      secondary?: string;
    };
    behaviorHeart?: string;
    oneOnOneAccent?: string;
    destructiveAccent?: string;
  }

  interface Theme {
    logoSrc: string;
  }
  interface ThemeOptions {
    logoSrc?: string;
  }
}

export const naluV1Theme = createTheme({
  logoSrc: '/nalu/logo-waa-256.png',
  palette: {
    primary: {
      dark: '#46776F',
      main: '#5E948C',
      light: '#79B5AC',
      contrastText: '#000000', // white fails WCAG AA here (3.45:1); black passes (6.09:1)
    },
    secondary: {
      dark: '#C05F47',
      main: '#D97B62',
      light: '#E8967E',
      contrastText: '#000000', // white fails even at the darkest coral shade, dark (4.23:1); black passes on main (6.96:1)
    },
    background: {
      default: '#FCF8F1', // Paper
      paper: '#F8F1E4', // Cream
    },
    gradient: {
      primary: 'linear-gradient(135deg, #46776F 0%, #D97B62 100%)',
      secondary: 'linear-gradient(135deg, #FCF8F1 0%, #F0E3CD 100%)',
    },
    behaviorHeart: '#D97B62', // "Hearts & celebration = coral" per the brand sheet's alignment rule
    oneOnOneAccent: '#79B5AC', // sea-teal light — distinct from default/alt's cyan
    destructiveAccent: '#C05F47', // secondary.dark — distinct from default/alt's deep orange
    success: {
      dark: '#46776F',
      main: '#5E948C', // "Star" reuses sea-teal per the brand kit's mockups, not a distinct hue
      light: '#79B5AC',
      contrastText: '#000000',
    },
    warning: {
      dark: '#B5853C',
      main: '#DFAE5B',
      light: '#E3BA73', // computed lighten of #DFAE5B — brand kit only defines two sand-gold shades
      contrastText: '#000000', // white fails (2.03:1); black passes (10.34:1)
    },
    error: {
      dark: '#C05F47',
      main: '#D97B62', // "Storm = coral, never pure red" per the brand sheet's alignment rule
      light: '#E8967E',
      contrastText: '#000000',
    },
    info: {
      dark: '#79B5AC',
      main: '#9BD0C2',
      light: '#C9E5DD', // brand kit's "sea light"
      contrastText: '#000000',
    },
  },
  typography: {
    fontFamily: '"Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Newsreader", serif', fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontFamily: '"Newsreader", serif', fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontFamily: '"Newsreader", serif', fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontFamily: '"Newsreader", serif', fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontFamily: '"Newsreader", serif', fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.5 },
    h6: { fontFamily: '"Newsreader", serif', fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 16, // brand sheet: "rounded 16–20px radii, soft single shadows"
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.12)' },
        },
        contained: { '&:hover': { boxShadow: '0 6px 16px rgba(0,0,0,0.16)' } },
        sizeSmall: { padding: '6px 16px', fontSize: '0.75rem' },
        sizeLarge: { padding: '12px 32px', fontSize: '1rem' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5E948C' },
            '&.Mui-focused': {
              '& input': { outline: 'none', border: 'none', boxShadow: 'none' },
              '& .MuiInputAdornment-root': { border: 'none', outline: 'none' },
            },
            '& input': {
              border: 'none',
              boxShadow: 'none',
              '&:focus': { outline: 'none', border: 'none', boxShadow: 'none' },
            },
          },
        },
      },
    },
    MuiPaper: {
      // Flat fill + hairline border instead of a drop-shadow (issue #1900) — matches the
      // design mockups, which never use box-shadow for depth on cards/panels.
      styleOverrides: {
        root: { borderRadius: 20, border: '1px solid #EAD9B9' },
        elevation1: { boxShadow: 'none' },
        elevation2: { boxShadow: 'none' },
        elevation3: { boxShadow: 'none' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid #EAD9B9',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontSize: '0.75rem', fontWeight: 500 } },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 0 },
        indicator: { display: 'none' }, // pill nav (issue #1900) carries selection via fill, not an underline
        flexContainer: { justifyContent: 'center', gap: 8 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: '9px 22px',
          borderRadius: 999,
          fontSize: '0.875rem',
          fontWeight: 700,
          textTransform: 'none',
          color: '#6E6258',
          backgroundColor: '#F8F1E4',
          border: '1px solid #EAD9B9',
          '&.Mui-selected': {
            color: '#F7EFDC',
            backgroundColor: '#46776F',
            fontWeight: 800,
            border: '1px solid transparent',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 16 } },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontSize: '1rem', fontWeight: 500 } },
    },
  },
});

export default naluV1Theme;
